import React, { useState } from "react";
import {
    Autocomplete,
    Box,
    IconButton,
    ListItem,
    TextField,
    Button,
} from "@mui/material";
import {
    RemoveCircleRounded as RemoveCircleRoundedIcon,
    AddCircleRounded as AddCircleRoundedIcon,
} from "@mui/icons-material";

interface GridAddAutocompleteProps {
    type: "line" | "category" | "section";
    disabled?: boolean;
    serviceItemOptions: string[];
    onAdd: (value: string) => void;
}

export const GridAddAutocomplete: React.FC<GridAddAutocompleteProps> = ({
    type,
    disabled,
    serviceItemOptions,
    onAdd,
}) => {
    const [adding, setAdding] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [highlightedValue, setHighlightedValue] = useState("");

    const handleCancelButtonClick = () => {
        setAdding(false);
        setInputValue("");
        setHighlightedValue("");
    };

    const addItem = (value: string) => {
        if (!value.trim()) return;
        setAdding(false);
        onAdd(value);
        setInputValue("");
        setHighlightedValue("");
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 1,
            }}
        >
            <Box>
                {adding ? (
                    <IconButton
                        size="small"
                        onClick={handleCancelButtonClick}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <RemoveCircleRoundedIcon fontSize="small" />
                    </IconButton>
                ) : (
                    <Button
                        disabled={disabled}
                        startIcon={
                            <AddCircleRoundedIcon
                                color={disabled ? "secondary" : "success"}
                            />
                        }
                        onClick={() => setAdding(true)}
                        color="info"
                        onPointerDown={(e) => e.stopPropagation()}
                        sx={{
                            fontSize: "10px",
                            padding: "2px 8px",
                            minWidth: "auto",
                            textTransform: "none",
                        }}
                    >
                        <span style={{ fontSize: 12 }}>Add {type}</span>
                    </Button>
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 150 }}>
                {adding && (
                    <Autocomplete
                        style={{ padding: 0 }}
                        value={inputValue}
                        disableClearable
                        freeSolo
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                placeholder={`Add ${type}`}
                                autoFocus
                                {...params}
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    style: {
                                        fontSize: 12,
                                        border: "none",
                                        padding: 2,
                                        paddingLeft: 6,
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    style: { padding: 0, fontSize: 12 },
                                }}
                            />
                        )}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                        }}
                        options={serviceItemOptions}
                        onPointerDown={(e) => {
                            e.stopPropagation();
                        }}
                        onChange={(e, v) => {
                            setInputValue(v || "");
                        }}
                        onInputChange={(e, v) => {
                            setInputValue(v || "");
                        }}
                        onHighlightChange={(e, v) => {
                            setHighlightedValue(v || "");
                        }}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                addItem(
                                    highlightedValue ||
                                        inputValue ||
                                        (e.target as HTMLInputElement).value,
                                );
                            }
                        }}
                        renderOption={(props, option) => (
                            <ListItem
                                {...props}
                                key={option}
                                style={{
                                    padding: 5,
                                    paddingLeft: 10,
                                    fontSize: 12,
                                }}
                                onPointerDown={(event) =>
                                    event.stopPropagation()
                                }
                            >
                                {option}
                            </ListItem>
                        )}
                    />
                )}
            </Box>
        </Box>
    );
};
